import { useState, useEffect } from 'react';
import { User, Mail, Building, Shield, Lock, Save, Eye, EyeOff, CheckCircle, Smartphone, KeyRound, QrCode, AlertTriangle } from 'lucide-react';
import { authAPI, mfaAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Defined OUTSIDE component so React doesn't remount on every render
function PwInput({ value, onChange, placeholder, show, onToggle, testId }) {
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="w-full border dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:bg-slate-900 dark:text-white pr-10"
        data-testid={testId}
      />
      <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function ProfilePage() {
  const { user } = useAuth();
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [pwChanged, setPwChanged] = useState(false);

  // MFA state — read from live API to avoid stale localStorage data
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaMethod, setMfaMethod] = useState('authenticator');
  const [mfaStep, setMfaStep] = useState('idle'); // idle | choose | setup-totp | setup-email | disable
  const [qrCode, setQrCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [mfaOtp, setMfaOtp] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaStatusLoaded, setMfaStatusLoaded] = useState(false);

  // Always fetch fresh MFA status from the server to avoid stale localStorage cache
  useEffect(() => {
    mfaAPI.status().then(res => {
      setMfaEnabled(res.data.mfa_enabled || false);
      setMfaMethod(res.data.mfa_method || 'authenticator');
      setMfaStatusLoaded(true);
    }).catch(() => {
      // Fallback to user object from context
      setMfaEnabled(user?.mfa_enabled || false);
      setMfaMethod(user?.mfa_method || 'authenticator');
      setMfaStatusLoaded(true);
    });
  }, []); // eslint-disable-line

  const roleName = user?.role_name || user?.role?.name || 'End User';
  const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || 'User';
  const dept = user?.department_name || user?.department?.name || '—';

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password) {
      toast.error('All password fields are required'); return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('New passwords do not match'); return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters'); return;
    }
    setSaving(true);
    try {
      await authAPI.changePassword({ old_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Password changed successfully');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      setPwChanged(true);
      setTimeout(() => setPwChanged(false), 5000);
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.old_password?.[0] || err.response?.data?.new_password?.[0] || 'Failed to change password';
      toast.error(msg);
    } finally { setSaving(false); }
  };

  // ── MFA handlers ────────────────────────────────────────────────────────────
  const startSetupTotp = async () => {
    setMfaLoading(true);
    try {
      const res = await mfaAPI.setupTotp();
      setQrCode(res.data.qr_code);
      setTotpSecret(res.data.manual_entry);
      setMfaStep('setup-totp');
    } catch { toast.error('Failed to start TOTP setup'); }
    finally { setMfaLoading(false); }
  };

  const startSetupEmail = async () => {
    setMfaLoading(true);
    try {
      await mfaAPI.sendEmailOtp({});
      setMfaStep('setup-email');
      toast.success(`OTP sent to ${user?.email}`);
    } catch { toast.error('Failed to send OTP'); }
    finally { setMfaLoading(false); }
  };

  const handleConfirmTotp = async () => {
    if (!mfaOtp) { toast.error('Enter the code'); return; }
    setMfaLoading(true);
    try {
      await mfaAPI.confirmTotp(mfaOtp);
      setMfaEnabled(true);
      setMfaMethod('authenticator');
      setMfaStep('idle');
      setMfaOtp('');
      toast.success('Authenticator MFA enabled!');
    } catch (err) { toast.error(err.response?.data?.error || 'Invalid code'); }
    finally { setMfaLoading(false); }
  };

  const handleConfirmEmail = async () => {
    if (!mfaOtp) { toast.error('Enter the OTP'); return; }
    setMfaLoading(true);
    try {
      await mfaAPI.confirmEmail(mfaOtp);
      setMfaEnabled(true);
      setMfaMethod('email');
      setMfaStep('idle');
      setMfaOtp('');
      toast.success('Email OTP MFA enabled!');
    } catch (err) { toast.error(err.response?.data?.error || 'Invalid OTP'); }
    finally { setMfaLoading(false); }
  };

  const handleDisableMfa = async () => {
    if (!disablePassword) { toast.error('Enter your password'); return; }
    setMfaLoading(true);
    try {
      await mfaAPI.disable(disablePassword);
      setMfaEnabled(false);
      setMfaStep('idle');
      setDisablePassword('');
      toast.success('MFA disabled');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to disable MFA'); }
    finally { setMfaLoading(false); }
  };

  return (
    <div data-testid="profile-page" className="max-w-2xl mx-auto space-y-6">
      <div className="mb-2">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <User className="w-6 h-6 text-sky-500" /> My Profile
        </h2>
        <p className="text-sm text-slate-500 mt-1">View your account details and manage your password</p>
      </div>

      {/* Profile Details Card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-sky-500 to-sky-600 px-6 py-8 flex items-center gap-5">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-white font-bold text-2xl">
            {fullName.charAt(0).toUpperCase()}
          </div>
          <div>
            <h3 className="text-xl font-bold text-white" data-testid="profile-fullname">{fullName}</h3>
            <span className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs px-3 py-1 rounded-full mt-1">
              <Shield className="w-3 h-3" />
              {roleName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-1">
                <Mail className="w-3.5 h-3.5" /> Email Address
              </div>
              <p className="font-medium text-slate-800 dark:text-white text-sm" data-testid="profile-email">{user?.email || '—'}</p>
            </div>
            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-1">
                <User className="w-3.5 h-3.5" /> Username
              </div>
              <p className="font-medium text-slate-800 dark:text-white text-sm" data-testid="profile-username">@{user?.username || '—'}</p>
            </div>
            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-1">
                <Building className="w-3.5 h-3.5" /> Department
              </div>
              <p className="font-medium text-slate-800 dark:text-white text-sm" data-testid="profile-department">{dept}</p>
            </div>
            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-1">
                <Shield className="w-3.5 h-3.5" /> Role
              </div>
              <p className="font-medium text-slate-800 dark:text-white text-sm" data-testid="profile-role">
                {roleName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6">
        <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 mb-5">
          <Lock className="w-4 h-4 text-sky-500" /> Change Password
          {pwChanged && <span className="ml-2 flex items-center gap-1 text-emerald-600 text-sm font-normal"><CheckCircle className="w-4 h-4" /> Changed successfully</span>}
        </h3>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">Current Password</label>
            <PwInput value={pwForm.current_password} onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} placeholder="Enter current password" show={showPw.current} onToggle={() => setShowPw(p => ({ ...p, current: !p.current }))} testId="current-password" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">New Password</label>
            <PwInput value={pwForm.new_password} onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} placeholder="Enter new password (min 8 chars)" show={showPw.new} onToggle={() => setShowPw(p => ({ ...p, new: !p.new }))} testId="new-password" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">Confirm New Password</label>
            <PwInput value={pwForm.confirm_password} onChange={e => setPwForm(p => ({ ...p, confirm_password: e.target.value }))} placeholder="Confirm new password" show={showPw.confirm} onToggle={() => setShowPw(p => ({ ...p, confirm: !p.confirm }))} testId="confirm-password" />
          </div>

          {pwForm.new_password && pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password && (
            <p className="text-xs text-rose-500">Passwords do not match</p>
          )}

          <button
            type="submit" disabled={saving}
            className="bg-sky-500 text-white px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600 disabled:bg-slate-300"
            data-testid="change-password-btn"
          >
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            Update Password
          </button>
        </form>
      </div>

      {/* ── Security Section Label ── */}
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
        <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Security</span>
        <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
      </div>

      {/* MFA Card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6" data-testid="mfa-section">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
            <Shield className="w-4 h-4 text-sky-500" /> Two-Factor Authentication (MFA)
          </h3>
          {!mfaStatusLoaded && <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />}
          {mfaStatusLoaded && mfaEnabled && (
            <span className="inline-flex items-center gap-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs px-3 py-1 rounded-full font-medium">
              <CheckCircle className="w-3.5 h-3.5" />
              {mfaMethod === 'authenticator' ? 'Authenticator App' : 'Email OTP'}
            </span>
          )}
        </div>

        {mfaStep === 'idle' && (
          <div className="space-y-3">
            {mfaEnabled ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  MFA is active using <strong>{mfaMethod === 'authenticator' ? 'Google Authenticator' : 'Email OTP'}</strong>.
                  You will be prompted for a code every time you sign in.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setMfaStep('choose')} className="bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 border border-sky-200 dark:border-sky-800 px-4 py-2 rounded-xl text-sm hover:bg-sky-100 transition-colors" data-testid="change-mfa-btn">
                    Change MFA Method
                  </button>
                  <button onClick={() => setMfaStep('disable')} className="bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 px-4 py-2 rounded-xl text-sm hover:bg-rose-100 transition-colors" data-testid="disable-mfa-btn">
                    Disable MFA
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Add an extra layer of security to your account. Choose between a mobile authenticator app or email-based OTP.
                </p>
                <button onClick={() => setMfaStep('choose')} className="bg-sky-500 text-white px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600 transition-colors" data-testid="enable-mfa-btn">
                  <Shield className="w-4 h-4" /> Enable MFA
                </button>
              </div>
            )}
          </div>
        )}

        {mfaStep === 'choose' && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Choose your preferred MFA method:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={startSetupTotp} disabled={mfaLoading}
                className="flex items-start gap-3 border-2 border-sky-200 dark:border-sky-800 rounded-xl p-4 hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors text-left"
                data-testid="choose-authenticator-btn">
                <Smartphone className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-slate-800 dark:text-white text-sm">Authenticator App</div>
                  <div className="text-xs text-slate-500 mt-0.5">Google Authenticator, Authy, etc. Most secure.</div>
                </div>
              </button>
              <button onClick={startSetupEmail} disabled={mfaLoading}
                className="flex items-start gap-3 border-2 border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                data-testid="choose-email-btn">
                <Mail className="w-5 h-5 text-slate-600 dark:text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-slate-800 dark:text-white text-sm">Email OTP</div>
                  <div className="text-xs text-slate-500 mt-0.5">Get a code sent to your email on each login.</div>
                </div>
              </button>
            </div>
            <button onClick={() => setMfaStep('idle')} className="text-slate-500 hover:text-slate-700 text-sm mt-2">Cancel</button>
          </div>
        )}

        {mfaStep === 'setup-totp' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Scan this QR code with Google Authenticator or Authy, then enter the 6-digit code to confirm.
            </p>
            {qrCode && (
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <img src={qrCode} alt="QR Code" className="w-40 h-40 rounded-xl border border-slate-200 dark:border-slate-700" data-testid="totp-qr-code" />
                <div className="flex-1 space-y-2">
                  <p className="text-xs text-slate-500">Can't scan? Enter this key manually:</p>
                  <code className="text-xs bg-slate-100 dark:bg-slate-900 px-3 py-2 rounded-lg block font-mono text-slate-700 dark:text-slate-300 break-all">{totpSecret}</code>
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Enter 6-digit code from app</label>
              <input type="text" inputMode="numeric" maxLength={6} value={mfaOtp} onChange={e => setMfaOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full border dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:bg-slate-900 dark:text-white font-mono tracking-widest text-center text-lg"
                data-testid="totp-confirm-input" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleConfirmTotp} disabled={mfaLoading || !mfaOtp}
                className="bg-sky-500 text-white px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600 disabled:bg-slate-300" data-testid="confirm-totp-btn">
                {mfaLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Confirm & Enable
              </button>
              <button onClick={() => { setMfaStep('idle'); setMfaOtp(''); }} className="text-slate-500 hover:text-slate-700 px-4 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        )}

        {mfaStep === 'setup-email' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              An OTP has been sent to <span className="font-medium text-sky-600 dark:text-sky-400">{user?.email}</span>. Enter it below to enable email-based MFA.
            </p>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Enter OTP from email</label>
              <input type="text" inputMode="numeric" maxLength={6} value={mfaOtp} onChange={e => setMfaOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full border dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:bg-slate-900 dark:text-white font-mono tracking-widest text-center text-lg"
                data-testid="email-otp-confirm-input" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={handleConfirmEmail} disabled={mfaLoading || !mfaOtp}
                className="bg-sky-500 text-white px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600 disabled:bg-slate-300" data-testid="confirm-email-otp-btn">
                {mfaLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Confirm & Enable
              </button>
              <button onClick={startSetupEmail} disabled={mfaLoading} className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2.5 rounded-xl text-sm">Resend to {user?.email}</button>
              <button onClick={() => { setMfaStep('idle'); setMfaOtp(''); }} className="text-slate-500 hover:text-slate-700 px-4 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        )}

        {mfaStep === 'disable' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-400">Disabling MFA reduces your account security. Enter your password to confirm.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Enter your current password</label>
              <PwInput value={disablePassword} onChange={e => setDisablePassword(e.target.value)} placeholder="Current password" show={showPw.current} onToggle={() => setShowPw(p => ({ ...p, current: !p.current }))} testId="disable-mfa-password" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleDisableMfa} disabled={mfaLoading || !disablePassword}
                className="bg-rose-500 text-white px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-rose-600 disabled:bg-slate-300" data-testid="confirm-disable-mfa-btn">
                {mfaLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Shield className="w-4 h-4" />}
                Disable MFA
              </button>
              <button onClick={() => { setMfaStep('idle'); setDisablePassword(''); }} className="text-slate-500 hover:text-slate-700 px-4 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
